import { QueryDocumentSnapshot } from "firebase/firestore";
import { DocumentData, DocumentSnapshot, Timestamp } from "shared/api/firebase";
import { Translations } from "shared/types/languages";
import {
  GamesOrderType,
  ModuleContentBlocksContent,
  ModuleData,
  ModuleLayout,
  ModuleStatus,
  ModuleType,
  BonusObtainType,
  ModuleIcon,
  ModuleTopPrivacyTipsBlockContent,
  ModuleGame,
  TagMatchRule,
} from "../types";

type ModuleDataDB = {
  name: string;
  type: ModuleType;
  layout: ModuleLayout;
  status: ModuleStatus;
  adminNotes: string | null;
  translations: Translations;
  tagTypes: string[];
  tags: string[];
  gamesOrderType: GamesOrderType | null;
  manualSelectedGames: ModuleGame[];
  tagSelectedGames: ModuleGame[];
  selectedGames: ModuleGame[];
  selectedStudiosIds: string[];
  orderedGamesStudiosIds: string[];
  selectedGamesIdsByStudio: Record<string, string[]>;
  studioFilterEnabled: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  baseUrl?: string;
  rows?: number;
  contentBlocks?: ModuleContentBlocksContent[];
  topPrivacyTipsBlock?: ModuleTopPrivacyTipsBlockContent;
  bonusObtainType?: BonusObtainType | null;
  icon?: ModuleIcon | null;
  tagsMatchRules?: TagMatchRule[];
};

export class ModuleDB {
  id: string;
  name: string;
  type: ModuleType;
  layout: ModuleLayout;
  status: ModuleStatus;
  adminNotes: string | null;
  translations?: Translations;
  tagTypes: string[];
  tags: string[];
  gamesOrderType: GamesOrderType | null;
  manualSelectedGames: ModuleGame[];
  tagSelectedGames: ModuleGame[];
  selectedGames: ModuleGame[];
  selectedStudiosIds: string[];
  orderedGamesStudiosIds: string[];
  selectedGamesIdsByStudio: Record<string, string[]>;
  studioFilterEnabled: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  baseUrl?: string;
  rows?: number;
  contentBlocks?: ModuleContentBlocksContent[];
  topPrivacyTipsBlock?: ModuleTopPrivacyTipsBlockContent;
  bonusObtainType?: BonusObtainType | null;
  icon: ModuleIcon | null;
  tagsMatchRules?: TagMatchRule[];

  constructor(module: QueryDocumentSnapshot | DocumentSnapshot<DocumentData>) {
    if (!module.exists()) {
      throw new Error(`Module ${module.id} does not exist`);
    }

    const data = module.data() as ModuleDataDB;

    this.id = module.id;
    this.name = data.name;
    this.type = data.type;
    this.layout = data.layout;
    this.status = data.status;
    this.adminNotes = data.adminNotes;
    this.translations = data.translations;
    this.tagTypes = data.tagTypes;
    this.tags = data.tags;
    this.gamesOrderType = data.gamesOrderType ?? null;
    this.selectedGames = data.selectedGames;
    this.manualSelectedGames = data.manualSelectedGames;
    this.tagSelectedGames = data.tagSelectedGames;
    this.selectedStudiosIds = data.selectedStudiosIds ?? [];
    this.orderedGamesStudiosIds = data.orderedGamesStudiosIds ?? [];
    this.selectedGamesIdsByStudio = data.selectedGamesIdsByStudio ?? {};
    this.studioFilterEnabled = data.studioFilterEnabled ?? false;
    this.createdAt = data.createdAt;
    this.updatedAt = data.updatedAt;
    this.baseUrl = data?.baseUrl;
    this.rows = data?.rows;
    this.contentBlocks = data?.contentBlocks;
    this.topPrivacyTipsBlock = data?.topPrivacyTipsBlock;
    this.bonusObtainType = data?.bonusObtainType ?? null;
    this.icon = data?.icon ?? null;
    this.tagsMatchRules = data?.tagsMatchRules ?? [];
  }

  toModule(): ModuleData {
    return {
      id: this.id,
      name: this.name,
      type: this.type,
      layout: this.layout,
      status: this.status,
      adminNotes: this.adminNotes,
      translations: this.translations,
      tagTypes: this.tagTypes,
      tags: this.tags,
      rows: this.rows,
      gamesOrderType: this.gamesOrderType,
      manualSelectedGames: this.manualSelectedGames,
      tagSelectedGames: this.tagSelectedGames,
      selectedGames: this.selectedGames,
      selectedStudiosIds: this.selectedStudiosIds,
      orderedGamesStudiosIds: this.orderedGamesStudiosIds,
      selectedGamesIdsByStudio: this.selectedGamesIdsByStudio,
      studioFilterEnabled: this.studioFilterEnabled,
      createdAt: this.createdAt.toDate(),
      updatedAt: this.updatedAt.toDate(),
      baseUrl: this.baseUrl,
      contentBlocks: this.contentBlocks,
      topPrivacyTipsBlock: this.topPrivacyTipsBlock,
      bonusObtainType: this.bonusObtainType,
      icon: this.icon,
      tagsMatchRules: this.tagsMatchRules,
    };
  }
}
